import React from 'react';
import { Modal, Badge, Group, Button, Stack, Box, Text, Pagination, Tooltip } from '@mantine/core';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import EntityBrowserTable from '../entities/EntityBrowserTable';
import type { PaginatedArrayRefViewerState } from '../../hooks/usePaginatedArrayRefViewer';
import { normalizeKey } from '../../types';
import { DialogErrorAlert } from './DialogErrorAlert';

export interface PaginatedArrayRefViewerModalProps {
    viewer: PaginatedArrayRefViewerState | null;
    schemaAttributes: any[];
    viewerEntities: any[];
    viewerLoading: boolean;
    viewerError: string | null;
    viewerPage: number;
    viewerTotalPages: number;
    viewerTotalResults: number;
    viewerSelectedIds: string[];
    viewerSelectedEntities: any[];
    viewerExtraColumnAttributes: string[];
    onPageChange: (page: number) => void;
    onEntitySelect: (id: string, isSelected: boolean) => void;
    onSelectAll: () => void;
    onRemoveSelected: (field: string) => void;
    onClearSelection: () => void;
    onClose: () => void;
}

export const PaginatedArrayRefViewerModal: React.FC<PaginatedArrayRefViewerModalProps> = ({
    viewer,
    schemaAttributes,
    viewerEntities,
    viewerLoading,
    viewerError,
    viewerPage,
    viewerTotalPages,
    viewerTotalResults,
    viewerSelectedIds,
    viewerSelectedEntities,
    viewerExtraColumnAttributes,
    onPageChange,
    onEntitySelect,
    onSelectAll,
    onRemoveSelected,
    onClearSelection,
    onClose,
}) => {
    const title = (() => {
        if (!viewer) return 'View';
        const attr = schemaAttributes?.find(
            (a: any) => normalizeKey((a.name || a.id) ?? '') === normalizeKey(viewer.field)
        );
        const fieldLabel = attr?.label ?? attr?.name ?? viewer.field ?? 'View';
        return viewer.mode === 'remove' ? `Remove items from ${fieldLabel}` : `View ${fieldLabel}`;
    })();

    return (
        <Modal
            opened={viewer != null}
            onClose={onClose}
            size={MODAL_SIZES.detail}
            title={
                <Tooltip label="PaginatedArrayRefViewerModal" withArrow position="top">
                    <span style={{ cursor: 'help' }}>
                        <Text fw={600} component="span">{title}</Text>
                    </span>
                </Tooltip>
            }
            styles={{ body: { minHeight: 320, display: 'flex', flexDirection: 'column' } }}
        >
            <Stack gap="md">
                <DialogErrorAlert error={viewerError} />
                {viewer?.mode === 'remove' && viewerSelectedIds.length > 0 && (
                    <Group align="center" gap="xs">
                        <Badge color="blue" size="sm">{viewerSelectedIds.length} selected</Badge>
                    </Group>
                )}
                {viewerTotalPages > 1 && (
                    <Pagination total={viewerTotalPages} value={viewerPage} onChange={onPageChange} disabled={viewerLoading} />
                )}
                <Box flex={1} mih={0}>
                    <EntityBrowserTable
                        entities={viewerEntities}
                        selectedIds={viewerSelectedIds}
                        loading={viewerLoading}
                        singleSelection={false}
                        sortBy="DisplayName"
                        sortOrder="asc"
                        searchQuery=""
                        onEntitySelect={onEntitySelect}
                        onSelectAll={onSelectAll}
                        onSortChange={() => {}}
                        getDisplayName={(e) =>
                            e.DisplayName ?? e.displayName ?? e.Name ?? e.name ?? e.Id ?? e.id ?? 'N/A'
                        }
                        getEntityType={(e) => e.EntityType ?? e.entityType ?? e.Type ?? e.type ?? ''}
                        alreadySelectedEntities={[]}
                        newlySelectedEntities={viewerSelectedEntities}
                        showSelectionColumn={viewer?.mode === 'remove'}
                        extraColumnAttributes={viewerExtraColumnAttributes}
                    />
                </Box>
                {viewerTotalPages > 1 && (
                    <Pagination total={viewerTotalPages} value={viewerPage} onChange={onPageChange} disabled={viewerLoading} />
                )}
                <Text size="sm" c="dimmed">
                    Showing {viewerEntities.length} of {viewerTotalResults} entities
                </Text>
                <Group justify="flex-end" gap="xs">
                    {viewerSelectedIds.length > 0 && viewer?.mode === 'remove' && viewer?.field && (
                        <Button
                            variant="light"
                            color="red"
                            onClick={() => {
                                onRemoveSelected(viewer.field);
                                onClearSelection();
                            }}
                        >
                            Remove selected
                        </Button>
                    )}
                    <Button variant="default" onClick={onClose}>
                        Close
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
